import { HttpErrorResponse, HttpParams, HttpResponse } from '@angular/common/http';
import { AbstractControl, ValidationErrors } from '@angular/forms';
import { catchError, from, map, Observable, of } from 'rxjs';
import { SALES_TEAM_DESIGNATIONS } from './common/helpers/constants';
import { Employee } from './employees/employees.model';
import { UserRole } from './common/models/common.model';
import { User } from './authentication/auth.models';

/**
 * Who the server's IsAdminUser permission lets through. `is_staff` is the flag Django
 * itself checks; the admin role is carried separately by users provisioned through the
 * app, so both count.
 */
export function isAdminUser(user: User | null | undefined): boolean {
  return !!user && (user.is_staff || user.role === UserRole.ADMIN);
}

export const SnackbarAlert = {
  INVALID_FORM: 'Please fill in all required fields.',
  INVALID_CREDENTIALS: 'Please provide valid credentials.',
  SOMETHING_WENT_WRONG: 'Something went wrong. Please try again.',
};

export const SnackbarAlertStatus = {
  SUCCESS: ['snackbar-success'],
  ERROR: ['snackbar-error'],
};

export enum SidebarState {
  IN = 'in',
  OUT = 'out',
}

const AVATAR_COLORS = [
  '#E57373',
  '#F06292',
  '#BA68C8',
  '#9575CD',
  '#7986CB',
  '#64B5F6',
  '#4FC3F7',
  '#4DD0E1',
  '#4DB6AC',
  '#81C784',
  '#AED581',
  '#FFD54F',
  '#FFB74D',
  '#FF8A65',
  '#A1887F',
];

function getColorForName(name: string): string {
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  }
  return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length];
}

function escapeXml(str: string): string {
  return str.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}

function getInitial(name: string): string {
  // Skip non-letter characters to find the first real letter
  const match = name.match(/[a-zA-Z]/);
  return match ? match[0] : '';
}

export function getDefaultProfilePicUrl(firstName: string, lastName: string = ''): string {
  const initials = (getInitial(firstName ?? '') + getInitial(lastName ?? '')).toUpperCase() || '?';
  const fullName = `${firstName ?? ''} ${lastName ?? ''}`.trim();
  const bgColor = getColorForName(fullName || 'Unknown');
  const safeInitials = escapeXml(initials);

  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="100" height="100" viewBox="0 0 100 100">
    <clipPath id="circle"><circle cx="50" cy="50" r="50"/></clipPath>
    <circle cx="50" cy="50" r="50" fill="${bgColor}" clip-path="url(#circle)"/>
    <text x="50" y="50" dy=".35em" text-anchor="middle" fill="white" font-family="Arial,sans-serif" font-size="40" font-weight="500">${safeInitials}</text>
  </svg>`;

  return `data:image/svg+xml;base64,${btoa(svg)}`;
}

export function handleImageError(event: any): void {
  const img = event.target as HTMLImageElement;
  if (!img || img.src.startsWith('data:')) return;

  const name = img.getAttribute('alt') || 'Unknown';
  const [first, ...rest] = name.split(' ');
  img.src = getDefaultProfilePicUrl(first, rest.join(' '));
}

export function timeToPercent(time: string): number {
  const [h, m] = time.split(':').map(Number);
  const totalMinutes = h * 60 + m;
  const minutesSinceStart = totalMinutes - 9 * 60; // 9:00 AM
  return (minutesSinceStart / (9 * 60)) * 100;
}

export function sessionPercentages(checkIn: string, checkOut: string) {
  const start = timeToPercent(checkIn);
  const end = timeToPercent(checkOut);
  return {
    left: `${start.toFixed(2)}%`,
    width: `${(end - start).toFixed(2)}%`,
    checkInPercent: `${start.toFixed(2)}%`,
    checkOutPercent: `${end.toFixed(2)}%`,
  };
}

export function isObjectEmpty(obj: object): boolean {
  return Object.values(obj).every(v => !v);
}

export function buildFilterQuery(filters: any): Record<string, string> {
  const query: Record<string, string> = {};

  Object.entries(filters).forEach(([key, value]) => {
    if (value !== null && value !== undefined && value !== '') {
      query[key] = String(value);
    }
  });

  return query;
}

export function buildHttpParams(query: Record<string, string> | null): HttpParams | undefined {
  if (!query) return undefined;

  let httpParams = new HttpParams();

  for (const [key, value] of Object.entries(query)) {
    if (!value) {
      continue;
    }

    if (value !== '') {
      httpParams = httpParams.set(key, value);
    }
  }

  return httpParams.keys().length ? httpParams : undefined;
}

export function toTitleCase(str: string): string {
  return str.replace(/\w\S*/g, txt => txt.charAt(0).toUpperCase() + txt.substring(1).toLowerCase());
}

export function isInSalesTeamEmployee(employee: Employee): boolean {
  console.log('employee', employee);
  return !!(employee?.designation?.title && SALES_TEAM_DESIGNATIONS.includes(employee?.designation?.title));
}

/**
 * Numeric values arrive from the API as decimal strings ("18.00"). Keeps 0 as 0
 * instead of collapsing it to null the way a falsy check would.
 */
export function toNumberOrNull(value: unknown): number | null {
  if (value === null || value === undefined || value === '') return null;

  const num = Number(value);
  return Number.isFinite(num) ? num : null;
}

/** Rounds half-up to `decimals` places, matching the backend's decimal rounding. */
export function roundHalfUp(value: number, decimals: number = 2): number {
  if (!Number.isFinite(value)) return NaN;

  const factor = 10 ** decimals;
  // toPrecision absorbs float noise (100.49999999999999 -> 100.5) so .005 rounds up.
  const scaled = Number((Math.abs(value) * factor).toPrecision(12));

  return (Math.sign(value) * Math.round(scaled)) / factor;
}

export function formatAmount(value: unknown): string {
  const num = toNumberOrNull(value);
  if (num === null) return '—';

  return `₹${new Intl.NumberFormat('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(num)}`;
}

/**
 * Money formatting for API decimal STRINGS (DRF serializes DecimalField as a string).
 * Everything below is pure string manipulation — the value never passes through a JS
 * float, so a ledger balance can never be silently misreported by binary rounding.
 * Use these instead of `formatAmount` wherever the source is a decimal string.
 */
const DECIMAL_STRING = /^(-)?(\d+)(?:\.(\d+))?$/;

/** Indian digit grouping (last 3, then pairs): 3000 -> 3,000; 1234567 -> 12,34,567. */
function groupIndianDigits(digits: string): string {
  if (digits.length <= 3) return digits;

  const lastThree = digits.slice(-3);
  const rest = digits.slice(0, -3);

  return `${rest.replace(/\B(?=(\d{2})+(?!\d))/g, ',')},${lastThree}`;
}

/** -1, 0 or 1 for a decimal string, decided by inspecting the characters. */
export function decimalSign(value: string | null | undefined): -1 | 0 | 1 {
  const match = String(value ?? '')
    .trim()
    .match(DECIMAL_STRING);
  if (!match) return 0;

  const isZero = !/[1-9]/.test(match[2] + (match[3] ?? ''));
  if (isZero) return 0;

  return match[1] ? -1 : 1;
}

/** Drops a leading minus without touching the digits. */
export function absDecimalString(value: string | null | undefined): string {
  return String(value ?? '')
    .trim()
    .replace(/^-/, '');
}

/**
 * Formats a decimal string as ₹ currency, preserving the exact digits the API sent.
 * Extra decimal places are truncated rather than rounded — displaying fewer digits
 * than were sent is safe, inventing a rounded value is not.
 */
export function formatDecimalAmount(value: string | null | undefined, fallback: string = '—'): string {
  if (value === null || value === undefined || String(value).trim() === '') return fallback;

  const raw = String(value).trim();
  const match = raw.match(DECIMAL_STRING);
  // Unrecognised shape: show it verbatim rather than risk mangling a real figure.
  if (!match) return raw;

  const [, sign = '', integerPart, fractionPart = ''] = match;

  return `${sign}₹${groupIndianDigits(integerPart)}.${(fractionPart + '00').slice(0, 2)}`;
}

/**
 * Formats a decimal quantity string, keeping the digits the API sent. Unlike an amount,
 * a quantity reads better without a fixed 2-decimal tail, so "70.00" becomes "70" while
 * "70.50" keeps its half. Never routed through a JS float.
 */
export function formatDecimalQuantity(value: string | null | undefined, fallback: string = '—'): string {
  if (value === null || value === undefined || String(value).trim() === '') return fallback;

  const raw = String(value).trim();
  const match = raw.match(DECIMAL_STRING);
  // Unrecognised shape: show it verbatim rather than risk mangling a real figure.
  if (!match) return raw;

  const [, sign = '', integerPart, fractionPart = ''] = match;
  const fraction = fractionPart.replace(/0+$/, '');

  return `${sign}${groupIndianDigits(integerPart)}${fraction ? `.${fraction}` : ''}`;
}

/**
 * Same as formatDecimalQuantity but always carries an explicit sign, for ledger-style
 * columns where "+25" and "-25" must never be mistaken for each other.
 */
export function formatSignedDecimalQuantity(value: string | null | undefined, fallback: string = '—'): string {
  const formatted = formatDecimalQuantity(value, '');
  if (!formatted) return fallback;

  return decimalSign(value) > 0 ? `+${formatted}` : formatted;
}

/** Blank is allowed (backend defaults to 0.00); otherwise 0-100 with up to 2 decimals. */
export function gstPercentageValidator(control: AbstractControl): ValidationErrors | null {
  const value = control.value;
  if (value === null || value === undefined || value === '') return null;

  const num = Number(value);
  if (!Number.isFinite(num)) return { gstPercentage: true };
  if (num < 0 || num > 100) return { gstPercentage: true };
  if ((String(value).split('.')[1] ?? '').length > 2) return { gstPercentage: true };

  return null;
}

/**
 * Guards against saving an error payload to disk as a spreadsheet: these endpoints
 * answer with a binary body on success but JSON on 401/403.
 */
export function isFileBlobResponse(response: HttpResponse<Blob>): boolean {
  if (!response.ok || !response.body || response.body.size === 0) return false;

  return !(response.body.type || '').includes('json');
}

/** Saves an attachment response, preferring the filename the server stamped into Content-Disposition. */
export function downloadBlobResponse(response: HttpResponse<Blob>, fallbackFilename: string): void {
  const disposition = response.headers.get('Content-Disposition') ?? '';
  const match = disposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
  const filename = match?.[1]?.replace(/['"]/g, '').trim() || fallbackFilename;

  const url = URL.createObjectURL(response.body!);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  anchor.click();
  URL.revokeObjectURL(url);
}

/**
 * Errors from a `responseType: 'blob'` request arrive as a Blob, so the JSON
 * `detail` has to be read out of it before it can be shown to the user.
 */
export function blobErrorMessage(error: HttpErrorResponse, fallback: string): Observable<string> {
  const body = error?.error;

  if (!(body instanceof Blob)) {
    return of(body?.detail || fallback);
  }

  return from(body.text()).pipe(
    map(text => {
      try {
        return JSON.parse(text)?.detail || fallback;
      } catch {
        return fallback;
      }
    }),
    catchError(() => of(fallback))
  );
}

export function convertUTCtoIST(time: string | null): string {
  if (!time) {
    return '';
  }

  const [hours, minutes, seconds] = time.split(':').map(Number);

  const date = new Date();
  date.setUTCHours(hours, minutes, seconds);

  return date.toLocaleTimeString('en-IN', {
    timeZone: 'Asia/Kolkata',
    hour: '2-digit',
    minute: '2-digit',
  });
}
